import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Text, Flex, HStack, Grid, GridItem, Heading,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import StatusBadge from '../components/ui/StatusBadge';
import TestCaseDrawer from '../components/TestCaseDrawer';
import client from '../utils/fetchClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fmtDuration = (ms) => {
  if (!ms || Number.isNaN(Number(ms))) return '-';
  const s = Math.round(Number(ms) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};
const fmtMs = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms || 0)}ms`);

function useRunDetail(runId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!runId) return;
    setLoading(true);
    setError(null);
    client.get(`${API_BASE_URL}/api/test-runs/${runId}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then((json) => { setData(json); setLoading(false); })
      .catch((err) => { setError(err.message || 'Error fetching run details'); setLoading(false); });
  }, [runId]);

  return { data, loading, error };
}

const TAB_KEYS = ['failed', 'flaky', 'all', 'skipped'];

function CaseRow({ tc, onOpen }) {
  const dot = tc.status === 'failed' ? 'status.fail'
    : tc.status === 'flaky' ? 'status.flaky'
    : tc.status === 'skipped' ? 'status.skip' : 'status.pass';
  return (
    <Grid
      templateColumns="10px 1fr auto"
      gap={3}
      alignItems="center"
      px={4}
      py={3}
      border="1px solid"
      borderColor="gray.100"
      borderRadius="12px"
      cursor="pointer"
      onClick={onOpen}
      _hover={{ bg: 'gray.50', borderColor: 'gray.200' }}
    >
      <Box w="8px" h="8px" borderRadius="full" bg={dot} />
      <Box minW={0}>
        <Text fontSize="13.5px" fontWeight="600" color="gray.900" noOfLines={1}>{tc.title}</Text>
        <Text fontSize="11px" color="gray.400" fontFamily="mono" mt="2px" noOfLines={1}>
          {tc.file || tc.suite || ''}{tc.line ? `:${tc.line}` : ''}
        </Text>
      </Box>
      <HStack spacing={2.5} whiteSpace="nowrap">
        <StatusBadge status={tc.status} />
        <Text fontSize="11.5px" color="gray.400" fontFamily="mono">{fmtMs(tc.duration)}</Text>
      </HStack>
    </Grid>
  );
}

export default function RunDetailPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useRunDetail(runId);
  const [tab, setTab] = useState('failed');
  const [drawerCase, setDrawerCase] = useState(null);

  const testRun = data?.testRun;
  const cases = useMemo(() => data?.testCases || [], [data]);

  const counts = useMemo(() => ({
    failed: cases.filter((c) => c.status === 'failed').length,
    flaky: cases.filter((c) => c.status === 'flaky').length,
    skipped: cases.filter((c) => c.status === 'skipped').length,
    all: cases.length,
  }), [cases]);

  const clusters = useMemo(() => {
    const map = {};
    cases.filter((c) => c.status === 'failed').forEach((c) => {
      const key = (c.file || c.suite || 'unknown').split('/').pop();
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [cases]);

  if (loading) return <LoadingState message="Loading run details" minH="60vh" />;
  if (error) return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  if (!testRun) return <ErrorState message="Test run not found." />;

  const visible = tab === 'all' ? cases : cases.filter((c) => c.status === tab);
  const total = (testRun.passedTests || 0) + (testRun.failedTests || 0) + (testRun.skippedTests || 0) || 1;
  const pct = (n) => `${((100 * (n || 0)) / total).toFixed(1)}%`;
  const label = { failed: `Failed ${counts.failed}`, flaky: `Flaky ${counts.flaky}`, all: `All ${counts.all}`, skipped: `Skipped ${counts.skipped}` };

  return (
    <Page maxW="7xl">
      <Breadcrumbs
        items={[
          { label: 'Metrics', to: '/metrics' },
          { label: 'Test runs', to: '/test-runs' },
          { label: `Run #${String(testRun.id).slice(0, 8)}` },
        ]}
      />

      <SectionCard mb={5}>
        <Flex align="center" gap={3} wrap="wrap">
          <Box w="10px" h="10px" borderRadius="full" bg={testRun.failedTests > 0 ? 'status.fail' : 'status.pass'} />
          <Heading size="md" fontWeight="800" letterSpacing="-0.3px" color="gray.900">
            {testRun.name || 'Test Run'} <Text as="span" color="gray.400" fontWeight="600">#{String(testRun.id).slice(0, 8)}</Text>
          </Heading>
          <StatusBadge status={testRun.failedTests > 0 ? 'failed' : 'passed'} />
          <Box flex="1" />
          <Button size="sm" variant="outline" onClick={() => navigate(`/compare?runA=${testRun.id}`)}>Compare</Button>
        </Flex>

        <Flex gap={5} wrap="wrap" mt={3.5} fontSize="12.5px" color="gray.500">
          {testRun.branch && <Text>branch <Text as="span" fontFamily="mono" color="gray.700">{testRun.branch}</Text></Text>}
          {testRun.commit && <Text>commit <Text as="span" fontFamily="mono" color="brand.600">{String(testRun.commit).slice(0, 8)}</Text></Text>}
          {testRun.environment && <Text>env <Text as="span" color="gray.700">{testRun.environment}</Text></Text>}
          <Text>started <Text as="span" color="gray.700">{testRun.startTime ? new Date(testRun.startTime).toLocaleString() : '-'}</Text></Text>
          <Text>duration <Text as="span" fontFamily="mono" color="gray.700">{fmtDuration(testRun.totalDuration)}</Text></Text>
        </Flex>

        <Flex align="center" gap={4} mt={4} wrap="wrap">
          <Flex h="8px" w="320px" maxW="100%" borderRadius="full" overflow="hidden" bg="gray.100">
            <Box w={pct(testRun.passedTests)} bg="status.pass" />
            <Box w={pct(testRun.failedTests)} bg="status.fail" />
            <Box w={pct(testRun.skippedTests)} bg="status.skip" />
          </Flex>
          <Text fontSize="12.5px" fontWeight="600" color="status.pass">{testRun.passedTests || 0} passed</Text>
          <Text fontSize="12.5px" fontWeight="600" color="status.fail">{testRun.failedTests || 0} failed</Text>
          <Text fontSize="12.5px" fontWeight="600" color="status.skip">{testRun.skippedTests || 0} skipped</Text>
        </Flex>
      </SectionCard>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 300px' }} gap={5} alignItems="start">
        <GridItem>
          <SectionCard>
            <HStack spacing={0} bg="gray.100" border="1px solid" borderColor="gray.200" borderRadius="full" p="3px" mb={4} w="fit-content">
              {TAB_KEYS.map((k) => {
                const active = k === tab;
                return (
                  <Button
                    key={k}
                    onClick={() => setTab(k)}
                    variant="unstyled"
                    h="28px"
                    px={4}
                    borderRadius="full"
                    fontSize="12px"
                    fontWeight={active ? '700' : '600'}
                    color={active ? 'brand.600' : 'gray.500'}
                    bg={active ? 'white' : 'transparent'}
                    boxShadow={active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'}
                    display="flex"
                    alignItems="center"
                  >
                    {label[k]}
                  </Button>
                );
              })}
            </HStack>

            <Flex direction="column" gap={2}>
              {visible.length === 0 ? (
                <Text fontSize="sm" color="gray.400" py={4}>No {tab === 'all' ? '' : tab} test cases.</Text>
              ) : (
                visible.map((tc) => (
                  <CaseRow key={tc.id} tc={tc} onOpen={() => setDrawerCase({ ...tc, testRunId: testRun.id })} />
                ))
              )}
            </Flex>
          </SectionCard>
        </GridItem>

        <GridItem>
          <Flex direction="column" gap={4}>
            <SectionCard>
              <Text fontSize="13px" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em" color="gray.400" mb={3}>
                Run summary
              </Text>
              <Flex direction="column" gap={2.5} fontSize="12.5px">
                <Flex justify="space-between"><Text color="gray.500">Total tests</Text><Text fontWeight="700">{testRun.totalTests}</Text></Flex>
                <Flex justify="space-between"><Text color="gray.500">Failed</Text><Text fontWeight="700" color="status.fail">{testRun.failedTests || 0}</Text></Flex>
                <Flex justify="space-between"><Text color="gray.500">Flaky</Text><Text fontWeight="700" color="status.flaky">{counts.flaky}</Text></Flex>
                <Flex justify="space-between"><Text color="gray.500">Duration</Text><Text fontWeight="700">{fmtDuration(testRun.totalDuration)}</Text></Flex>
              </Flex>
            </SectionCard>

            {clusters.length > 0 && (
              <SectionCard>
                <Text fontSize="13px" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em" color="gray.400" mb={3}>
                  Failure clusters
                </Text>
                <Flex direction="column" gap={2.5} fontSize="12.5px">
                  {clusters.map(([file, n]) => (
                    <Flex key={file} justify="space-between" gap={2}>
                      <Text color="gray.700" fontFamily="mono" noOfLines={1}>{file}</Text>
                      <Text fontWeight="700" color="status.fail">{n}</Text>
                    </Flex>
                  ))}
                </Flex>
              </SectionCard>
            )}
          </Flex>
        </GridItem>
      </Grid>

      <TestCaseDrawer open={!!drawerCase} onClose={() => setDrawerCase(null)} testCase={drawerCase} />
    </Page>
  );
}
